/**
 * useRecommendations — fetches contextual recommendations for the current
 * branch + cart (FR-019 Phase 1, online surface).
 *
 * Mirrors the plain useEffect/useState hook style of `usePromotions`.
 * Refetches when the cart contents change (debounced 400ms so qty-stepper
 * taps don't cause a request storm). Any failure — or the backend's normal
 * kill-switch/degraded empty response — resolves to `items: []` so callers
 * simply render nothing. Recommendations must never break a screen.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import {
  recommendationsApi,
  type RecommendationItem,
  type RecommendationTrigger,
} from "@/api/recommendationsApi";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { useCart } from "@/store/cart";

export type UseRecommendationsResult = {
  items: RecommendationItem[];
  loading: boolean;
};

const DEBOUNCE_MS = 400;

export function useRecommendations(
  trigger: RecommendationTrigger,
  opts: { limit?: number } = {},
): UseRecommendationsResult {
  const { branchId } = useBranch();
  const { cart } = useCart();
  const { user } = useAuth();
  const [items, setItems] = useState<RecommendationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const limit = opts.limit ?? 5;
  const customerId = user?.id ?? undefined;

  // Stable key over cart contents (item ids + quantities) so the effect only
  // refires when what's in the cart actually changes, not on every render.
  const cartKey = useMemo(
    () =>
      cart
        .map((l) => `${l.itemId}:${l.qty}`)
        .sort()
        .join("|"),
    [cart],
  );

  // Read the latest cart from a ref inside the debounced timeout without
  // making `cart` (a new array every render) an effect dependency.
  const cartRef = useRef(cart);
  cartRef.current = cart;

  useEffect(() => {
    if (!branchId) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      const cartLines = cartRef.current.map((l) => ({
        itemId: l.itemId,
        quantity: l.qty,
      }));
      recommendationsApi
        .fetch({ branchId, trigger, cart: cartLines, customerId, limit })
        .then((res) => {
          if (cancelled) return;
          setItems(Array.isArray(res?.items) ? res.items : []);
          setLoading(false);
        })
        .catch(() => {
          // Degrade silently — the strip hides itself on empty items.
          if (cancelled) return;
          setItems([]);
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [branchId, trigger, cartKey, customerId, limit]);

  return { items, loading };
}
